import { getDb } from './index';
import { selectRemoteDirect, syncExecuteRemote } from './tursoSync';

export interface GuestMigrationResult {
  migrated: boolean;
  type: 'auto_uploaded' | 'cloud_loaded' | 'merge_prompt_needed' | 'already_migrated' | 'no_local_data';
  remoteCount?: number;
  localCount?: number;
}

/**
 * Recorre todas las tablas locales y las inserta/actualiza en la base de datos remota de Turso.
 */
export async function syncAllLocalDataToRemote(db: any): Promise<void> {
  const tables = [
    { name: 'facultades', keys: ['id', 'nombre', 'sigla', 'disponible', 'escala_calificacion_id', 'terminologia', 'design_tokens_ref'] },
    { name: 'escalas_calificacion', keys: ['id', 'facultad', 'nombre', 'niveles'] },
    { name: 'carreras', keys: ['id', 'facultad_id', 'nombre', 'tipo', 'creditos_totales', 'duracion_semestres', 'locacion', 'malla_precargada'] },
    { name: 'planes_estudio', keys: ['id', 'nombre', 'facultad', 'duracion_semestres'] },
    { name: 'modulos', keys: ['id', 'facultad_id', 'nombre', 'creditos_minimos', 'color_hex'] },
    { name: 'ciclos', keys: ['id', 'facultad_id', 'nombre', 'creditos_minimos', 'orden'] },
    { name: 'asignaturas', keys: ['id', 'plan_id', 'nombre', 'codigo', 'semestre_sugerido', 'creditos', 'departamento', 'facultad_origen', 'estado', 'requisitos', 'periodo_cursado', 'fecha_inicio', 'fecha_fin', 'fecha_aprobacion', 'ano_cursado', 'nota_final', 'porcentaje_logro', 'concepto_final', 'posicion_x', 'posicion_y', 'eva_url', 'sala9_url', 'en_papelera', 'es_opcional', 'dictado', 'previas_sugeridas', 'calificacion_oficial', 'aplazos', 'modulo_id', 'ciclo_id'] },
    { name: 'acreditaciones_libres', keys: ['id', 'usuario_id', 'facultad_id', 'modulo_id', 'nombre_actividad', 'creditos', 'fecha_acreditacion'] },
    { name: 'evaluaciones', keys: ['id', 'asignatura_id', 'tipo', 'nombre', 'fecha', 'porcentaje_logro', 'concepto', 'nota', 'escala'] },
    { name: 'materiales', keys: ['id', 'asignatura_id', 'tipo', 'ruta_relativa', 'nombre', 'tags', 'fecha_agregado', 'url_r2'] },
    { name: 'carpetas_notas', keys: ['id', 'nombre', 'color', 'padre_id', 'fecha_creacion'] },
    { name: 'notas_generales', keys: ['id', 'titulo', 'contenido', 'asignatura_id', 'carpeta_id', 'tags', 'fecha_creacion', 'fecha_modificacion', 'en_papelera'] },
    { name: 'horarios_clases', keys: ['id', 'asignatura_id', 'dia', 'hora_inicio', 'hora_fin', 'grupo', 'salon', 'docente', 'observaciones', 'periodo'] },
    { name: 'eventos_calendario', keys: ['id', 'tipo', 'asignatura_id', 'fecha', 'descripcion', 'hora'] },
    { name: 'historial_academico', keys: ['id', 'asignatura_id', 'fecha', 'instancia', 'resultado', 'calificacion'] },
  ];

  // Obtener sets de IDs de entidades locales para evitar subir referencias huérfanas que violen FOREIGN KEYs
  const localAsigIds = new Set<string>();
  const localPlanIds = new Set<string>();
  const localFolderIds = new Set<string>();
  const localModuloIds = new Set<string>();
  const localCicloIds = new Set<string>();

  try {
    const localPlans = await db.select('SELECT id FROM planes_estudio');
    localPlans.forEach((p: any) => localPlanIds.add(p.id));
  } catch (_) {}

  try {
    const localAsigs = await db.select('SELECT id FROM asignaturas');
    localAsigs.forEach((a: any) => localAsigIds.add(a.id));
  } catch (_) {}

  try {
    const localFolders = await db.select('SELECT id FROM carpetas_notas');
    localFolders.forEach((f: any) => localFolderIds.add(f.id));
  } catch (_) {}

  try {
    const localModulos = await db.select('SELECT id FROM modulos');
    localModulos.forEach((m: any) => localModuloIds.add(m.id));
  } catch (_) {}

  try {
    const localCiclos = await db.select('SELECT id FROM ciclos');
    localCiclos.forEach((c: any) => localCicloIds.add(c.id));
  } catch (_) {}

  for (const t of tables) {
    try {
      const rows = await db.select(`SELECT * FROM ${t.name}`);
      for (const row of rows) {
        // Asegurar que si una asignatura referencia a un plan inexistente, se cree el plan por defecto
        if (t.name === 'asignaturas') {
          if (row.plan_id && !localPlanIds.has(row.plan_id)) {
            const defaultPlanSql = `INSERT OR REPLACE INTO planes_estudio (id, nombre, facultad, duracion_semestres) VALUES ($1, $2, $3, $4)`;
            await syncExecuteRemote(defaultPlanSql, [row.plan_id, 'Plan de Estudios', 'FCEA', 8]);
            localPlanIds.add(row.plan_id);
          }
          if (row.modulo_id && !localModuloIds.has(row.modulo_id)) {
            row.modulo_id = null;
          }
          if (row.ciclo_id && !localCicloIds.has(row.ciclo_id)) {
            row.ciclo_id = null;
          }
        }

        if (t.name === 'acreditaciones_libres') {
          if (row.modulo_id && !localModuloIds.has(row.modulo_id)) {
            row.modulo_id = null;
          }
        }

        // Sanitizar referencias huérfanas en tablas dependientes
        if (['evaluaciones', 'materiales', 'horarios_clases', 'eventos_calendario', 'historial_academico'].includes(t.name)) {
          if (row.asignatura_id && !localAsigIds.has(row.asignatura_id)) {
            // Si la asignatura ya no existe, omitir la subida de este ítem huérfano para evitar FOREIGN KEY error
            continue;
          }
        }

        if (t.name === 'notas_generales') {
          if (row.asignatura_id && !localAsigIds.has(row.asignatura_id)) {
            row.asignatura_id = null;
          }
          if (row.carpeta_id && !localFolderIds.has(row.carpeta_id)) {
            row.carpeta_id = null;
          }
        }

        const columns = t.keys.filter(k => row[k] !== undefined);
        const placeholders = columns.map((_, i) => `$${i + 1}`).join(', ');
        const values = columns.map(k => {
          const val = row[k];
          if (typeof val === 'boolean') return val ? 1 : 0;
          if (typeof val === 'object' && val !== null) return JSON.stringify(val);
          return val === undefined ? null : val;
        });

        const sql = `INSERT OR REPLACE INTO ${t.name} (${columns.join(', ')}) VALUES (${placeholders})`;
        await syncExecuteRemote(sql, values);
      }
    } catch (err) {
      console.warn(`[GuestMigration] Error syncing table ${t.name}:`, err);
    }
  }
}

/**
 * Descarga todos los datos remotos de Turso a la base SQLite local.
 */
export async function syncAllRemoteDataToLocal(db: any, remoteAsignaturas: any[] = []): Promise<void> {
  const tables = ['facultades', 'escalas_calificacion', 'carreras', 'planes_estudio', 'modulos', 'ciclos', 'asignaturas', 'acreditaciones_libres', 'evaluaciones', 'materiales', 'carpetas_notas', 'notas_generales', 'horarios_clases', 'eventos_calendario', 'historial_academico'];

  for (const table of tables) {
    try {
      let rows: any[] = [];
      if (table === 'asignaturas' && remoteAsignaturas.length > 0) {
        rows = remoteAsignaturas;
      } else {
        rows = await selectRemoteDirect(`SELECT * FROM ${table}`);
      }

      if (rows.length > 0) {
        await db.execute(`DELETE FROM ${table}`);
        for (const row of rows) {
          const keys = Object.keys(row);
          const placeholders = keys.map((_, i) => `$${i + 1}`).join(', ');
          const values = Object.values(row).map(val => (typeof val === 'object' && val !== null ? JSON.stringify(val) : val));
          const sql = `INSERT OR REPLACE INTO ${table} (${keys.join(', ')}) VALUES (${placeholders})`;
          await db.execute(sql, values);
        }
      }
    } catch (err) {
      console.warn(`[GuestMigration] Error syncing table ${table} to local:`, err);
    }
  }
}

/**
 * Evalúa y ejecuta el flujo de migración al loguearse/registrarse desde modo invitado.
 */
export async function checkAndMigrateGuestData(userId: string): Promise<GuestMigrationResult> {
  const migrationKey = `malla_guest_migrated_${userId}`;
  if (localStorage.getItem(migrationKey) === 'true') {
    return { migrated: false, type: 'already_migrated' };
  }

  const db = await getDb();

  // 1. Obtener conteo de asignaturas locales de invitado
  const localAsignaturas = await db.select<any[]>('SELECT * FROM asignaturas');
  const hasLocalGuestData = localAsignaturas.length > 0;

  // 2. Consultar asignaturas en la nube (Turso)
  const remoteAsignaturas = await selectRemoteDirect<any[]>('SELECT * FROM asignaturas');
  const hasRemoteCloudData = remoteAsignaturas.length > 0;

  if (!hasLocalGuestData && !hasRemoteCloudData) {
    localStorage.setItem(migrationKey, 'true');
    return { migrated: false, type: 'no_local_data' };
  }

  // CASO 1: Cuenta nueva en la nube (0 registros) Y hay datos locales de invitado
  // -> subir automáticamente todo a la base recién provisionada del usuario, sin pedir confirmación.
  if (!hasRemoteCloudData && hasLocalGuestData) {
    console.info('[GuestMigration] Caso 1: Subiendo automáticamente datos de invitado a la nube...');
    await syncAllLocalDataToRemote(db);
    localStorage.setItem(migrationKey, 'true');
    return { migrated: true, type: 'auto_uploaded', localCount: localAsignaturas.length };
  }

  // CASO 2: Cuenta con datos en la nube Y este dispositivo tiene datos locales de invitado
  // -> La nube manda, pero se alerta al usuario para dar opción de fusionar o descartar.
  if (hasRemoteCloudData && hasLocalGuestData) {
    console.info('[GuestMigration] Caso 2: Se detectaron datos en la nube y locales de invitado. Solicitando decisión...');
    return {
      migrated: false,
      type: 'merge_prompt_needed',
      remoteCount: remoteAsignaturas.length,
      localCount: localAsignaturas.length
    };
  }

  // CASO 3: Cuenta con datos en la nube Y sin datos de invitado en el dispositivo
  // -> Descargar automáticamente datos de la nube.
  if (hasRemoteCloudData && !hasLocalGuestData) {
    console.info('[GuestMigration] Caso 3: Cargando datos de la cuenta remota...');
    await syncAllRemoteDataToLocal(db, remoteAsignaturas);
    localStorage.setItem(migrationKey, 'true');
    return { migrated: true, type: 'cloud_loaded', remoteCount: remoteAsignaturas.length };
  }

  return { migrated: false, type: 'already_migrated' };
}

/**
 * Resuelve la decisión tomada en el caso 2 (Fusionar o Descartar).
 */
export async function resolveGuestMigrationDecision(userId: string, decision: 'merge' | 'discard', db?: any): Promise<void> {
  const database = db || await getDb();
  const migrationKey = `malla_guest_migrated_${userId}`;

  if (decision === 'merge') {
    // Subir los datos locales faltantes a la nube y luego traer todo
    await syncAllLocalDataToRemote(database);
    const updatedRemote = await selectRemoteDirect<any[]>('SELECT * FROM asignaturas');
    await syncAllRemoteDataToLocal(database, updatedRemote);
  } else {
    // Descartar locales: Sobrescribir todo localmente con lo que está en la nube
    const remoteAsignaturas = await selectRemoteDirect<any[]>('SELECT * FROM asignaturas');
    await syncAllRemoteDataToLocal(database, remoteAsignaturas);
  }

  localStorage.setItem(migrationKey, 'true');
}
